/*
 * Copyright (c) 2026. Tooling Team
 */

import { baseApi } from "./baseApi";

export const transformLabDomainListResponse = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.records)) {
    return response.records;
  }

  if (Array.isArray(response?.results)) {
    return response.results;
  }

  return [];
};

const PRODUCT_USAGE_MAP = [
  ["loincVc", "VC"],
  ["loincOptum", "LS"],
  ["loincCai", "CAI"],
];

export const transformLabDomainCodeMappingResponse = (response) =>
  transformLabDomainListResponse(response).map((record) => ({
    ...record,
    productUsage: PRODUCT_USAGE_MAP.filter(([key]) => record[key]).map(
      ([, label]) => label
    ),
  }));

export const transformLabDomainClientsResponse = transformLabDomainListResponse;

export const getLabDomainClientDropdownValues = (clientsResponse) =>
  transformLabDomainClientsResponse(clientsResponse)
    .map((clientOption) => {
      if (!clientOption?._id || !clientOption?.client) {
        return null;
      }

      return {
        id: clientOption._id,
        label: clientOption.client,
        value: clientOption._id,
      };
    })
    .filter(Boolean);

export const applyCodeMappingCachePatch =
  (body, loincConceptDescription) => (draft) => {
    body.forEach(({ _id, ...rest }) => {
      const row = draft.find((r) => r._id === _id);
      if (!row) return;
      const { userComment, loincCode, status } = rest;
      if (Object.prototype.hasOwnProperty.call(rest, "userComment")) {
        row.userComments = userComment;
      }
      if (loincCode) {
        row.loincConcept.code = loincCode;
        row.loincConcept.description = loincConceptDescription;
      }
      if (status) row.status = status;
    });
  };

export const getLabDomainMutationMapReference = (data) =>
  data?.mapUuid ?? data?.mapId ?? data?.mapReference;

const getOriginalMapReference = (originalArgs) =>
  typeof originalArgs === "object" && originalArgs !== null
    ? originalArgs.mapReference
    : originalArgs;

const containsPatchedRow = (query, body) => {
  if (query?.endpointName !== "getLabDomainCodeMapping" || !body.length) {
    return false;
  }

  const patchedRowIds = new Set(body.map(({ _id }) => _id).filter(Boolean));

  return (
    patchedRowIds.size > 0 &&
    Array.isArray(query.data) &&
    query.data.some((row) => patchedRowIds.has(row?._id))
  );
};

export const labDomainApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getLabDomainClients: builder.query({
      query: () => "/lab/v1/clients",
      transformResponse: transformLabDomainClientsResponse,
    }),
    getLabDomainCodeMapping: builder.query({
      query: (args) => {
        const queryArgs =
          typeof args === "object" && args !== null
            ? args
            : { mapReference: args };
        const {
          mapReference,
          version = "MAX",
          includeAllVersions = false,
        } = queryArgs;
        const searchParams = new URLSearchParams({ version });
        if (includeAllVersions) {
          searchParams.set("includeAllVersions", "true");
        }
        return `/lab/v1/code-mapping/${mapReference}?${searchParams.toString()}`;
      },
      transformResponse: transformLabDomainCodeMappingResponse,
    }),
    getLabDomainCodeMappingHistory: builder.query({
      query: (recordId) => `/lab/v1/code-mapping/${recordId}/history`,
      transformResponse: transformLabDomainListResponse,
    }),
    getLabDomainCodeEvidence: builder.query({
      query: ({ mapUuid, localSystem, localCode }) => ({
        url: "/lab/v1/code-evidence/search",
        method: "POST",
        body: {
          mapUuid,
          localSystem,
          localCode,
        },
      }),
      transformResponse: transformLabDomainListResponse,
    }),
    getLabDomainBuildsList: builder.query({
      query: () => "/lab/v1/builds/list",
      transformResponse: transformLabDomainListResponse,
    }),
    updateLabDomainCodeMapping: builder.mutation({
      query: ({ body }) => ({
        url: `/lab/v1/code-mapping`,
        method: "PATCH",
        body,
      }),
      transformResponse: (response) => response,
      async onQueryStarted(
        { body, loincConceptDescription },
        { dispatch, queryFulfilled, getState }
      ) {
        try {
          const { data } = await queryFulfilled;
          const { api: apiState } = getState();
          const cacheEntry = Object.values(apiState.queries).find((q) =>
            containsPatchedRow(q, body)
          );
          if (!cacheEntry) return;
          const originalArgs = cacheEntry.originalArgs;
          const originalMapReference = getOriginalMapReference(originalArgs);
          const responseMapReference = getLabDomainMutationMapReference(data);
          if (
            responseMapReference &&
            originalMapReference &&
            responseMapReference !== originalMapReference
          ) {
            return;
          }
          dispatch(
            labDomainApi.util.updateQueryData(
              "getLabDomainCodeMapping",
              originalArgs,
              applyCodeMappingCachePatch(body, loincConceptDescription)
            )
          );
        } catch {
          // mutation failed — cache unchanged
        }
      },
    }),
    publishLabDomainCodeMapping: builder.mutation({
      query: (mappingId) => ({
        url: `/lab/v1/code-mapping/${mappingId}/publish`,
        method: "POST",
      }),
    }),
  }),
});

export const {
  useGetLabDomainBuildsListQuery,
  useGetLabDomainClientsQuery,
  useGetLabDomainCodeEvidenceQuery,
  useGetLabDomainCodeMappingQuery,
  useGetLabDomainCodeMappingHistoryQuery,
  useLazyGetLabDomainCodeMappingQuery,
  usePublishLabDomainCodeMappingMutation,
  useUpdateLabDomainCodeMappingMutation,
} = labDomainApi;
